import React from 'react';
import { Box, Typography, Divider, Paper, Tooltip, IconButton } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import Card from './Card';  // Importing the Card component to display individual cards
import RiskCard from './RiskCard';
import IssueCard from './IssueCard'; //Importing the RiskCard component to display risk cards
import CRMCard from './CRMCard';

// Column component which takes in props for column data and card actions
const columnInfoMap = {
  'Risk Identification': 'Start here when you spot a possible risk. Anyone can add a card to let the team know about something that might cause problems.',
  'Analysis': 'Here, the team looks closer at each risk. We think about how likely it is, how big the impact could be, and what might cause it.',
  'Mitigation Planning': 'If a risk needs action, we make a plan here. The person in charge writes down what we will do to avoid or handle the risk.',
  'In-Progress': 'We are working on the plan for this risk. Cards stay here while actions are being taken.',
  'Monitoring': 'Some risks need to be watched over time. The team checks these regularly and adds updates.',
  'Done': 'These risks are finished. We either fixed them, they are not a problem anymore, or we decided not to do anything more.',
  'Blocked': 'We cannot move forward with these risks right now. Something is stopping us, like missing resources or waiting for someone else.',
  'Withdrawn': 'These risks are no longer important or have been combined with other risks, so we removed them from the board.'
};

const Column = ({
  column,
  cards,
  handleDragOver,
  handleDrop,
  handleDragStart,
  handleOpenEditDialog,
  handleDeleteCard,
  handleDuplicateCard,
  rules = [],
  dragOverCard = null,
  columns = [],
  isSprintContext,
  isRiskManagementTemplate = false,
  isIssueManagementTemplate = false,
  isCRMTemplate = false
}) => {
  // Filter and sort cards for this column
  const columnCards = cards
    .filter((card) => card.cardStatus === column.name)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return (
      <Paper
        key={column.id}
        sx={{
          flexShrink: 0,
          minWidth: 220,
          maxWidth: { xs: '100%', sm: 220 },
          minHeight: 500, // Minimum height to ensure columns are visible
          mx: 1,
          p: 1,
          borderRadius: 4,
          backgroundColor: '#fff',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          // Remove fixed height to allow natural expansion
        }}
        onDragOver={handleDragOver}
        onDrop={(event) => handleDrop(event, column)}
      >
        {/* Header section - fixed at top */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 1,
          flexShrink: 0 // Keep header fixed
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: '#333',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: (column.name === 'Risk Identification' || column.name === 'Mitigation Planning') 
                ? '0.85rem' 
                : '1rem',
            }}
          >
            {column.name}
          </Typography>
          {isRiskManagementTemplate && column.name !== 'Withdrawn' && column.name !== 'Done' && (
            <Tooltip title={columnInfoMap[column.name] || ''} arrow placement="top">
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoIcon sx={{ fontSize: '1rem', color: '#3173d8' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Divider sx={{ mb: 2, flexShrink: 0 }} />
        
        {/* Cards container - expands naturally with content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            // Remove overflow and maxHeight to allow natural expansion
            flexGrow: 1, // Take available space but allow expansion
          }}
        >
          {columnCards.map((card) => (
            card.cardType === 'Risk' ? (
              <RiskCard
                key={card.id}
                card={card}
                handleDragStart={handleDragStart}
                handleOpenEditDialog={handleOpenEditDialog}
                handleDeleteCard={handleDeleteCard}
                columns={columns}
              />
            ) : card.cardType === 'Issue' ? (
              <IssueCard
                key={card.id}
                card={card}
                handleDragStart={handleDragStart}
                handleOpenEditDialog={handleOpenEditDialog}
                handleDeleteCard={handleDeleteCard}
              />
            ) : card.cardType === 'CRM' ? (
              <CRMCard
                key={card.id}
                card={card}
                handleDragStart={handleDragStart}
                handleOpenEditDialog={handleOpenEditDialog}
                handleDeleteCard={handleDeleteCard}
                isDragOver={dragOverCard === card.id}
              />
            ) : (
              <Card
                key={card.id}
                card={card}
                handleDragStart={handleDragStart}
                handleOpenEditDialog={handleOpenEditDialog}
                handleDeleteCard={handleDeleteCard}
                handleDuplicateCard={handleDuplicateCard}
                rules={rules}
                isDragOver={dragOverCard === card.id}
                columns={columns}
                isSprintContext={isSprintContext}
              />
            )
          ))}
        </Box>
      </Paper>
  );
};

export default Column; // Exporting the Column component